import React, { useEffect, useRef, useState } from 'react';
import markerIcon from '../assets/map_marker/regular-marker.png';
import './BalloonPlayground.css';
import { heartOutline, evenlySpacedPoints } from './balloonHeart';

const interactive = 'a, button, input, textarea, select, [role="button"], [contenteditable="true"], [data-no-balloon]';
const label = '用故事连接彼此';

export default function BalloonPlayground({ children }) {
  const [released, setReleased] = useState([]);
  const [heart, setHeart] = useState(null);
  const clickCount = useRef(0);
  const celebrating = useRef(false);
  useEffect(() => {
    if (!heart) return;
    // Allow 700ms to gather, then hold the complete heart for three seconds.
    const timer = window.setTimeout(() => {
      setReleased([]);
      setHeart(null);
      clickCount.current = 0;
      celebrating.current = false;
    }, 3700);
    return () => window.clearTimeout(timer);
  }, [heart]);
  const cursor = useRef(null);
  const surface = useRef(null);
  const nextId = useRef(0);
  const hideCursor = () => {
    if (cursor.current) cursor.current.style.visibility = 'hidden';
    surface.current?.classList.remove('is-on-blank');
  };
  const moveCursor = event => {
    if (celebrating.current || event.pointerType === 'touch' || event.target.closest(interactive) || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      hideCursor(); return;
    }
    surface.current?.classList.add('is-on-blank');
    if (cursor.current) {
      cursor.current.style.visibility = 'visible';
      cursor.current.style.left = `${event.clientX}px`;
      cursor.current.style.top = `${event.clientY}px`;
    }
  };
  const release = event => {
    if (celebrating.current || event.defaultPrevented || event.target.closest(interactive)) return;
    const balloon = { id: ++nextId.current, x: event.clientX, y: event.clientY, duration: 18 + Math.random() * 8 };
    // Ephemeral decoration only: no storage, no database. Bound DOM size during rapid clicking.
    setReleased(previous => [...previous.slice(-119), balloon]);
    clickCount.current += 1;
    if (clickCount.current > 15) {
      celebrating.current = true;
      hideCursor();
      const width = window.innerWidth, height = window.innerHeight;
      const origins = Array.from(surface.current.querySelectorAll('[data-floating-balloon] img, .released-balloon img')).map(img => {
        const rect = img.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      });
      // The triggering balloon has not been rendered yet.
      origins.push({ x: balloon.x, y: balloon.y + 38 });
      const outline = heartOutline(width, height);
      const targets = evenlySpacedPoints(outline, origins.length);
      setHeart({ width, height, origins, targets });
    }
  };
  return <div ref={surface} className={`balloon-playground${heart ? ' is-heart' : ''}`} onPointerMove={moveCursor} onPointerLeave={hideCursor} onPointerCancel={hideCursor} onClick={release}>
    {children}
    <div ref={cursor} className="balloon-wordmark balloon-mouse" aria-hidden="true"><img src={markerIcon} alt=""/><span>{label}</span></div>
    {heart && <div className="balloon-heart" role="status" aria-label="气球连成一颗爱心">
      <svg aria-hidden="true" viewBox={`0 0 ${heart.width} ${heart.height}`}>
        {heart.origins.map((origin, index) => <image key={index} href={markerIcon} width={heart.width < 600 ? 26 : 40} height={heart.width < 600 ? 38 : 58}
          x={-(heart.width < 600 ? 13 : 20)} y={-(heart.width < 600 ? 19 : 29)} className="balloon-heart-node"
          style={{ '--from-x': `${origin.x}px`, '--from-y': `${origin.y}px`, '--to-x': `${heart.targets[index].x}px`, '--to-y': `${heart.targets[index].y}px` }} />)}
      </svg>
    </div>}
    <div className="released-balloons" aria-hidden="true">
      {released.map(balloon => <div key={balloon.id} className="released-balloon" style={{ left: balloon.x, top: balloon.y, '--rise-duration': `${balloon.duration}s`, '--rise-distance': `${-(balloon.y + 160)}px` }} onAnimationEnd={() => setReleased(previous => previous.filter(item => item.id !== balloon.id))}>
        <div className="balloon-wordmark"><img src={markerIcon} alt=""/><span>{label}</span></div>
      </div>)}
    </div>
  </div>;
}
